import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { 
  FiSearch, FiGrid, FiMessageSquare, FiHelpCircle, 
  FiSettings, FiChevronsLeft, FiChevronsRight, FiLogOut, 
  FiUser, FiLock, FiChevronUp, FiBook, FiUsers, FiMapPin, FiLoader
} from "react-icons/fi";
import { LuSparkles } from "react-icons/lu";

// Dynamic card accent palettes matching your original styling
const CARD_PALETTES = [
  {
    gradient: "from-blue-50 via-blue-50/30 to-transparent",
    border: "hover:border-blue-300",
    glow: "group-hover:shadow-[0_4px_20px_-4px_rgba(59,130,246,0.12)]",
    iconBg: "bg-blue-100 text-blue-600",
    activeBg: "bg-gradient-to-r from-blue-50 via-blue-50/50 to-white border-blue-200",
    activeBar: "bg-blue-600",
    activeIcon: "bg-blue-600 text-white shadow-md shadow-blue-500/20",
  },
  {
    gradient: "from-indigo-50 via-indigo-50/30 to-transparent",
    border: "hover:border-indigo-300",
    glow: "group-hover:shadow-[0_4px_20px_-4px_rgba(99,102,241,0.12)]",
    iconBg: "bg-indigo-100 text-indigo-600",
    activeBg: "bg-gradient-to-r from-indigo-50 via-indigo-50/50 to-white border-indigo-200",
    activeBar: "bg-indigo-600",
    activeIcon: "bg-indigo-600 text-white shadow-md shadow-indigo-500/20",
  },
  {
    gradient: "from-violet-50 via-violet-50/30 to-transparent",
    border: "hover:border-violet-300",
    glow: "group-hover:shadow-[0_4px_20px_-4px_rgba(139,92,246,0.12)]",
    iconBg: "bg-violet-100 text-violet-600",
    activeBg: "bg-gradient-to-r from-violet-50 via-violet-50/50 to-white border-violet-200",
    activeBar: "bg-violet-600",
    activeIcon: "bg-violet-600 text-white shadow-md shadow-violet-500/20",
  },
  {
    gradient: "from-emerald-50 via-emerald-50/30 to-transparent",
    border: "hover:border-emerald-300",
    glow: "group-hover:shadow-[0_4px_20px_-4px_rgba(16,185,129,0.12)]",
    iconBg: "bg-emerald-100 text-emerald-600",
    activeBg: "bg-gradient-to-r from-emerald-50 via-emerald-50/50 to-white border-emerald-200",
    activeBar: "bg-emerald-600",
    activeIcon: "bg-emerald-600 text-white shadow-md shadow-emerald-500/20",
  },
  {
    gradient: "from-amber-50 via-amber-50/30 to-transparent",
    border: "hover:border-amber-300",
    glow: "group-hover:shadow-[0_4px_20px_-4px_rgba(245,158,11,0.12)]",
    iconBg: "bg-amber-100 text-amber-600",
    activeBg: "bg-gradient-to-r from-amber-50 via-amber-50/50 to-white border-amber-200",
    activeBar: "bg-amber-600",
    activeIcon: "bg-amber-600 text-white shadow-md shadow-amber-500/20",
  },
];

export default function RedesignedLeftSidebar({
  permissions = [],
  user = {},
  teacherSubjects = [],
  classes = [],
  isLoading = false,
  handleLogout,
  onOpenChangePassword,
}) {
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [subjectSearch, setSubjectSearch] = useState("");
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const [activeTooltip, setActiveTooltip] = useState(null);

  const hasPerm = (perm) => permissions.includes(perm);
  const subjectList = teacherSubjects.length > 0 ? teacherSubjects : classes;
  const canShowSubjects = hasPerm("can_view_teacher_classroom") || subjectList.length > 0 || isLoading;

  useEffect(() => {
    function handleClickOutside(event) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const matchesSearch = (text) =>
    String(text || "").toLowerCase().includes(subjectSearch.toLowerCase());

  const handleMouseEnter = (e, title) => {
    if (!isCollapsed) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setActiveTooltip({
      title,
      top: rect.top + rect.height / 2,
      left: rect.right + 12,
    });
  };

  const handleMouseLeave = () => {
    setActiveTooltip(null);
  };

  const filteredTeacherSubjects = subjectList.filter((subj) => {
    const displayName = subj.display_name || subj.name || subj.title || subj.subject_name || "";
    const code = subj.subject_code || subj.code || "";
    const classroomName = subj.classroom_name || (subj.classroom ? subj.classroom.name : "");
    const roomNumber = subj.room_number || (subj.room ? subj.room.name || subj.room.number : "");

    return (
      matchesSearch(displayName) ||
      matchesSearch(code) ||
      matchesSearch(classroomName) ||
      matchesSearch(roomNumber)
    );
  });

  return (
    <aside
      className={`bg-white border-r border-slate-200/80 font-dynamic-title flex flex-col shrink-0 text-slate-600 text-xs font-medium z-20 transition-all duration-300 ease-in-out relative shadow-sm ${
        isCollapsed ? "w-16" : "w-full md:w-64"
      }`}
    >
      {/* Header Bar */}
      <div
        className={`p-3 border-b border-slate-200/80 flex items-center ${
          isCollapsed ? "justify-center" : "justify-between"
        }`}
      >
        {!isCollapsed && (
          <div className="flex items-center gap-2 px-1">
            <div className="w-2 h-2 rounded-full bg-blue-600 animate-pulse" />
            <span className="font-bold uppercase tracking-wider text-[11px] text-slate-800">
              Teacher Classroom
            </span>
          </div>
        )}

        <button
          onClick={() => {
            setIsCollapsed(!isCollapsed);
            setUserMenuOpen(false);
            setActiveTooltip(null);
          }}
          onMouseEnter={(e) =>
            handleMouseEnter(e, isCollapsed ? "Expand Sidebar" : "Collapse Sidebar")
          }
          onMouseLeave={handleMouseLeave}
          className="p-1.5 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-all duration-200 cursor-pointer hover:scale-105 active:scale-95"
        >
          {isCollapsed ? (
            <FiChevronsRight className="w-4 h-4" />
          ) : (
            <FiChevronsLeft className="w-4 h-4" />
          )}
        </button>
      </div>

      {/* Main Body */}
      <div className="p-2.5 flex-1 flex flex-col min-h-0 space-y-5 overflow-y-auto custom-scroll">
        {/* Navigation Section */}
        <div>
          {!isCollapsed && (
            <p className="px-2.5 mb-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
              Main Navigation
            </p>
          )}
          <div className="space-y-1">
            <Link
              to="/overview"
              onMouseEnter={(e) => handleMouseEnter(e, "Overview")}
              onMouseLeave={handleMouseLeave}
              className={`relative group w-full flex items-center rounded-xl transition-all duration-200 ${
                isCollapsed ? "justify-center p-2.5" : "gap-3 px-3 py-2"
              } ${
                location.pathname === "/overview"
                  ? "text-white bg-gradient-to-r from-blue-600 to-indigo-600 shadow-md shadow-blue-500/20 font-semibold"
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-100/80"
              }`}
            >
              <FiGrid
                className={`w-4 h-4 shrink-0 transition-transform duration-200 group-hover:scale-110 ${
                  location.pathname === "/overview"
                    ? "text-white"
                    : "text-slate-500 group-hover:text-slate-800"
                }`}
              />
              {!isCollapsed && <span className="text-xs">Overview</span>}
            </Link>
          </div>
        </div>

        {/* Assigned Classes Section */}
        {canShowSubjects && (
          <div className="flex-1 flex flex-col min-h-0 pt-1">
            {!isCollapsed && (
              <div className="px-1 mb-2.5">
                <div className="flex items-center justify-between mb-2 px-1">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1">
                    <LuSparkles className="w-3 h-3 text-amber-500" />
                    <span>Assigned Classes</span>
                  </p>
                  {isLoading ? (
                    <span className="flex items-center gap-1 text-[10px] text-blue-600 font-semibold">
                      <FiLoader className="w-3 h-3 animate-spin" />
                      Loading...
                    </span>
                  ) : (
                    <span className="text-[10px] bg-slate-100 text-slate-600 border border-slate-200 px-1.5 py-0.5 rounded-full font-mono">
                      {filteredTeacherSubjects.length}
                    </span>
                  )}
                </div>

                <div className="relative w-full group">
                  <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs transition-colors group-focus-within:text-blue-600" />
                  <input
                    type="text"
                    placeholder="Search classes or rooms..."
                    value={subjectSearch}
                    disabled={isLoading}
                    onChange={(e) => setSubjectSearch(e.target.value)}
                    className="w-full pl-8 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-500/10 transition-all placeholder:text-slate-400 text-slate-800 disabled:opacity-50"
                  />
                </div>
              </div>
            )}

            {/* Subject List or Loading Skeleton */}
            <div className="flex-1 overflow-y-auto space-y-2 pr-0.5 custom-scroll">
              {isLoading ? (
                /* Skeleton Loader State */
                Array.from({ length: 4 }).map((_, idx) => (
                  <div
                    key={idx}
                    className={`animate-pulse rounded-xl bg-slate-100 border border-slate-200/60 ${
                      isCollapsed ? "w-10 h-10 mx-auto" : "p-3 space-y-2"
                    }`}
                  >
                    {!isCollapsed && (
                      <>
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 bg-slate-200 rounded-lg shrink-0" />
                          <div className="flex-1 space-y-1">
                            <div className="h-3 bg-slate-200 rounded w-3/4" />
                            <div className="h-2 bg-slate-200 rounded w-1/2" />
                          </div>
                        </div>
                        <div className="h-2 bg-slate-200 rounded w-full pt-1" />
                      </>
                    )}
                  </div>
                ))
              ) : (
                filteredTeacherSubjects.map((subj, index) => {
                  const subjectDisplayName =
                    subj.display_name || subj.name || subj.title || subj.subject_name || "Classroom";
                  const subjectCode = subj.subject_code || subj.code || "";
                  const roomNumber =
                    subj.room_number || (subj.room ? subj.room.name || subj.room.number : null);
                  const totalStudents =
                    subj.total_students ?? (subj.students ? subj.students.length : null);

                  const targetPath = subj.path || `/academic/teacher/classroom/${subj.id}`;
                  const isActive = location.pathname === targetPath;
                  const palette = CARD_PALETTES[index % CARD_PALETTES.length];

                  if (isCollapsed) {
                    return (
                      <Link
                        key={subj.id || index}
                        to={targetPath}
                        onClick={(e) => subj.onClick && subj.onClick(e)}
                        onMouseEnter={(e) => handleMouseEnter(e, subjectDisplayName)}
                        onMouseLeave={handleMouseLeave}
                        className={`relative flex items-center justify-center w-10 h-10 mx-auto rounded-xl transition-all duration-300 group ${
                          isActive
                            ? `${palette.activeIcon}`
                            : "text-slate-500 hover:bg-slate-100 hover:text-slate-900 hover:scale-105"
                        }`}
                      >
                        <FiBook className="w-4 h-4 transition-transform group-hover:scale-110" />
                      </Link>
                    );
                  }

                  return (
                    <Link
                      key={subj.id || index}
                      to={targetPath}
                      onClick={(e) => subj.onClick && subj.onClick(e)}
                      className={`group relative block rounded-xl border p-3 transition-all duration-300 overflow-hidden ${
                        isActive
                          ? `${palette.activeBg} shadow-sm backdrop-blur-md`
                          : `bg-slate-50/60 border-slate-200/80 ${palette.border} ${palette.glow} hover:bg-gradient-to-r ${palette.gradient} hover:-translate-y-0.5`
                      }`}
                    >
                      {isActive && (
                        <span
                          className={`absolute left-0 top-0 bottom-0 w-1 ${palette.activeBar} rounded-r-full`}
                        />
                      )}

                      <div className="flex items-start justify-between gap-2 min-w-0">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div
                            className={`p-2 rounded-lg shrink-0 transition-all duration-300 group-hover:scale-105 ${
                              isActive
                                ? palette.activeIcon
                                : `${palette.iconBg} group-hover:bg-white group-hover:shadow-sm`
                            }`}
                          >
                            <FiBook className="w-3.5 h-3.5" />
                          </div>

                          <span
                            className={`truncate text-xs font-bold leading-tight transition-colors duration-200 ${
                              isActive ? "text-slate-900" : "text-slate-800 group-hover:text-slate-900"
                            }`}
                          >
                            {subjectDisplayName}
                          </span>
                        </div>

                        {subjectCode && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded-md bg-white text-slate-500 font-mono font-semibold border border-slate-200 shrink-0 group-hover:border-slate-300 transition-colors shadow-2xs">
                            {subjectCode}
                          </span>
                        )}
                      </div>

                      {(roomNumber || totalStudents !== null) && (
                        <div className="flex items-center justify-between mt-2.5 pt-2 border-t border-slate-200/60 text-[10px] text-slate-500">
                          {roomNumber && (
                            <span className="flex items-center gap-1 truncate">
                              <FiMapPin className="w-3 h-3 text-slate-400" />
                              <span>
                                Room:{" "}
                                <strong className="text-slate-700 font-medium">{roomNumber}</strong>
                              </span>
                            </span>
                          )}
                          {totalStudents !== null && (
                            <span className="flex items-center gap-1 ml-auto">
                              <FiUsers className="w-3 h-3 text-slate-400" />
                              <span>
                                Students:{" "}
                                <strong className="text-slate-700 font-medium">
                                  {totalStudents}
                                </strong>
                              </span>
                            </span>
                          )}
                        </div>
                      )}
                    </Link>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* Security Section */}
        {(hasPerm("can_view_user_list") || hasPerm("can_view_account_lock")) && (
          <div>
            {!isCollapsed && (
              <p className="px-2.5 mb-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                Security & Access
              </p>
            )}
            <div className="space-y-1">
              {hasPerm("can_view_user_list") && (
                <Link
                  to="/users"
                  onMouseEnter={(e) => handleMouseEnter(e, "User Accounts")}
                  onMouseLeave={handleMouseLeave}
                  className={`group w-full flex items-center rounded-xl text-slate-600 hover:text-slate-900 hover:bg-slate-100/80 transition-all duration-200 ${
                    isCollapsed ? "justify-center p-2.5" : "gap-3 px-3 py-2"
                  }`}
                >
                  <FiUser className="w-4 h-4 shrink-0 transition-transform group-hover:scale-110" />
                  {!isCollapsed && <span className="text-xs">User Accounts</span>}
                </Link>
              )}
              {hasPerm("can_view_account_lock") && (
                <Link
                  to="/account/locked"
                  onMouseEnter={(e) => handleMouseEnter(e, "Locked Accounts")}
                  onMouseLeave={handleMouseLeave}
                  className={`group w-full flex items-center rounded-xl text-slate-600 hover:text-slate-900 hover:bg-slate-100/80 transition-all duration-200 ${
                    isCollapsed ? "justify-center p-2.5" : "gap-3 px-3 py-2"
                  }`}
                >
                  <FiLock className="w-4 h-4 shrink-0 transition-transform group-hover:scale-110" />
                  {!isCollapsed && <span className="text-xs">Locked Accounts</span>}
                </Link>
              )}
            </div>
          </div>
        )}

        {/* Support Section */}
        <div>
          {!isCollapsed && (
            <p className="px-2.5 mb-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
              Support
            </p>
          )}
          <div className="space-y-1">
            <Link
              to="/support/feedback"
              onMouseEnter={(e) => handleMouseEnter(e, "Feedback")}
              onMouseLeave={handleMouseLeave}
              className={`group w-full flex items-center rounded-xl text-slate-600 hover:text-slate-900 hover:bg-slate-100/80 transition-all duration-200 ${
                isCollapsed ? "justify-center p-2.5" : "gap-3 px-3 py-2"
              }`}
            >
              <FiMessageSquare className="w-4 h-4 shrink-0 transition-transform group-hover:scale-110" />
              {!isCollapsed && <span className="text-xs">Feedback</span>}
            </Link>
            <Link
              to="/support/help"
              onMouseEnter={(e) => handleMouseEnter(e, "Help & Support")}
              onMouseLeave={handleMouseLeave}
              className={`group w-full flex items-center rounded-xl text-slate-600 hover:text-slate-900 hover:bg-slate-100/80 transition-all duration-200 ${
                isCollapsed ? "justify-center p-2.5" : "gap-3 px-3 py-2"
              }`}
            >
              <FiHelpCircle className="w-4 h-4 shrink-0 transition-transform group-hover:scale-110" />
              {!isCollapsed && <span className="text-xs">Help & Support</span>}
            </Link>
            <Link
              to="/configuration"
              onMouseEnter={(e) => handleMouseEnter(e, "Settings")}
              onMouseLeave={handleMouseLeave}
              className={`group w-full flex items-center rounded-xl text-slate-600 hover:text-slate-900 hover:bg-slate-100/80 transition-all duration-200 ${
                isCollapsed ? "justify-center p-2.5" : "gap-3 px-3 py-2"
              }`}
            >
              <FiSettings className="w-4 h-4 shrink-0 transition-transform group-hover:scale-110" />
              {!isCollapsed && <span className="text-xs">Settings</span>}
            </Link>
          </div>
        </div>
      </div>

      {/* Footer User Menu */}
      <div
        className="p-3 border-t border-slate-200/80 bg-slate-50/50 backdrop-blur-md relative"
        ref={userMenuRef}
      >
        {userMenuOpen && (
          <div
            className={`absolute bottom-full mb-3 bg-white border border-slate-200 rounded-2xl shadow-xl p-1.5 z-50 transition-all animate-in fade-in slide-in-from-bottom-2 duration-200 ${
              isCollapsed ? "left-14 w-52" : "left-2.5 right-2.5"
            }`}
          >
            <div className="px-3 py-2 border-b border-slate-100">
              <p className="text-xs font-bold text-slate-900 truncate">
                {user.first_name} {user.last_name}
              </p>
              <p className="text-[10px] text-slate-500 truncate">{user.email}</p>
            </div>

            <div className="p-1 space-y-1">
              <Link
                to="/my/profile"
                onClick={() => setUserMenuOpen(false)}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-xl transition-all"
              >
                <FiUser className="w-3.5 h-3.5 text-slate-400" />
                <span>My Profile</span>
              </Link>
              <div className="border-t border-slate-100 my-1" />

              <button
                onClick={() => {
                  setUserMenuOpen(false);
                  if (handleLogout) handleLogout();
                }}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-rose-600 hover:bg-rose-50 hover:text-rose-700 rounded-xl transition-all font-medium cursor-pointer"
              >
                <FiLogOut className="w-3.5 h-3.5" />
                <span>Log Out</span>
              </button>
            </div>
          </div>
        )}

        <button
          onMouseEnter={(e) =>
            handleMouseEnter(e, `${user.first_name || "User"} (${user.email || "Account"})`)
          }
          onMouseLeave={handleMouseLeave}
          onClick={() => {
            setUserMenuOpen(!userMenuOpen);
            setActiveTooltip(null);
          }}
          className={`w-full flex items-center rounded-xl hover:bg-slate-200/60 transition-all p-1.5 cursor-pointer group border border-transparent hover:border-slate-200 ${
            isCollapsed ? "justify-center" : "justify-between"
          }`}
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-xl bg-[#002855] text-white flex items-center justify-center font-bold text-xs shrink-0 shadow-sm group-hover:scale-105 transition-transform">
              {user.first_name ? user.first_name.slice(0, 2).toUpperCase() : "TC"}
            </div>
            {!isCollapsed && (
              <div className="min-w-0 text-left">
                <p className="text-xs font-bold text-slate-800 group-hover:text-slate-900 truncate leading-tight">
                  {user.last_name || user.first_name
                    ? `${user.last_name || ""}, ${user.first_name || ""}`
                    : "Teacher Account"}
                </p>
                <p className="text-[10px] text-slate-500 truncate leading-tight">
                  {user.email || "teacher@school.edu"}
                </p>
              </div>
            )}
          </div>
          {!isCollapsed && (
            <FiChevronUp
              className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${
                userMenuOpen ? "rotate-180" : ""
              }`}
            />
          )}
        </button>
      </div>

      {/* Floating Tooltip */}
      {isCollapsed && activeTooltip && (
        <div
          style={{
            top: `${activeTooltip.top}px`,
            left: `${activeTooltip.left}px`,
          }}
          className="fixed -translate-y-1/2 z-[9999] pointer-events-none flex items-center gap-1.5"
        >
          <div className="w-2 h-2 bg-slate-900 border-l border-b border-slate-800 rotate-45 -mr-1 shrink-0" />
          <div className="bg-slate-900 text-white shadow-xl rounded-xl px-3 py-1.5 text-xs font-semibold tracking-wide whitespace-nowrap">
            {activeTooltip.title}
          </div>
        </div>
      )}
    </aside>
  );
}